import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Star, GripVertical, Plus, X, Send, MessageSquareHeart } from 'lucide-react';
import MainMenu from '@components/ui/game/main-menu/MainMenu';
import useMetadataStore from '@/store/useMetadataStore';
import { submitBetaFeedback } from '@/api/betaFeedbackApi';
import feedback from '@components/ui/general/feedback/feedback';
import { useT } from '@/i18n';
import './BetaFeedback.css';

// The 8 beta units with their display info
const BETA_UNITS = [
  { id: 'guardian', name: 'Guardian', role: 'Tank', image: '/alberath/images/units/guardian_medium.webp' },
  { id: 'trent', name: 'Trent', role: 'Tank/Healer', image: '/alberath/images/units/trent_medium.webp' },
  { id: 'piromancer', name: 'Piromancer', role: 'DPS PSI', image: '/alberath/images/units/piromancer_medium.webp' },
  { id: 'archer', name: 'Archer', role: 'DPS Fisico', image: '/alberath/images/units/archer_medium.webp' },
  { id: 'sprite', name: 'Sprite', role: 'Soporte', image: '/alberath/images/units/sprite_medium.webp' },
  { id: 'bardo', name: 'Bardo', role: 'Buffer', image: '/alberath/images/units/bard_medium.webp' },
  { id: 'berserker', name: 'Berserker', role: 'DPS Melee', image: '/alberath/images/units/berserker_medium.webp' },
  { id: 'mentalist', name: 'Mentalist', role: 'Debuffer', image: '/alberath/images/units/mentalist_medium.webp' },
];

// Sortable unit item component
function SortableUnitItem({ unit, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: unit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`sortable-unit-item ${isDragging ? 'dragging' : ''}`} {...attributes} {...listeners}>
      <div className="unit-rank">#{index + 1}</div>
      <GripVertical className="drag-handle" size={20} />
      <img src={unit.image} alt={unit.name} className="unit-image" />
      <div className="unit-info">
        <span className="unit-name">{unit.name}</span>
        <span className="unit-role">{unit.role}</span>
      </div>
    </div>
  );
}

// Star rating component
function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`star-btn ${star <= (hovered || value) ? 'active' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
        >
          <Star size={32} fill={star <= (hovered || value) ? 'var(--color-gold)' : 'transparent'} />
        </button>
      ))}
    </div>
  );
}

// Combo builder component
function ComboBuilder({ combos, onCombosChange, t }) {
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [reason, setReason] = useState('');

  const addUnitToCombo = (unitId) => {
    if (!selectedUnits.includes(unitId)) {
      setSelectedUnits([...selectedUnits, unitId]);
    }
  };

  const removeUnitFromCombo = (unitId) => {
    setSelectedUnits(selectedUnits.filter(id => id !== unitId));
  };

  const saveCombo = () => {
    if (selectedUnits.length >= 2 && reason.trim()) {
      onCombosChange([...combos, { units: selectedUnits, reason: reason.trim() }]);
      setSelectedUnits([]);
      setReason('');
    }
  };

  const removeCombo = (index) => {
    onCombosChange(combos.filter((_, i) => i !== index));
  };

  const getUnitById = (id) => BETA_UNITS.find(u => u.id === id);

  return (
    <div className="combo-builder">
      {/* Saved combos */}
      {combos.length > 0 && (
        <div className="saved-combos">
          <h4>{t('feedback.saved_combos')}</h4>
          {combos.map((combo, index) => (
            <div key={index} className="saved-combo">
              <div className="combo-units">
                {combo.units.map(unitId => {
                  const unit = getUnitById(unitId);
                  return (
                    <div key={unitId} className="combo-unit-badge">
                      <img src={unit?.image} alt={unit?.name} />
                      <span>{unit?.name}</span>
                    </div>
                  );
                })}
              </div>
              <p className="combo-reason">{combo.reason}</p>
              <button type="button" className="remove-combo-btn" onClick={() => removeCombo(index)}>
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Combo creator */}
      <div className="combo-creator">
        <h4>{t('feedback.create_combo')}</h4>
        <p className="combo-hint">{t('feedback.combo_hint')}</p>

        {/* Unit selector */}
        <div className="unit-selector">
          {BETA_UNITS.map(unit => (
            <button
              key={unit.id}
              type="button"
              className={`unit-select-btn ${selectedUnits.includes(unit.id) ? 'selected' : ''}`}
              onClick={() => selectedUnits.includes(unit.id) ? removeUnitFromCombo(unit.id) : addUnitToCombo(unit.id)}
            >
              <img src={unit.image} alt={unit.name} />
              <span>{unit.name}</span>
            </button>
          ))}
        </div>

        {/* Selected units preview */}
        {selectedUnits.length > 0 && (
          <div className="selected-preview">
            <span>{t('feedback.combo')}: </span>
            {selectedUnits.map((unitId, idx) => {
              const unit = getUnitById(unitId);
              return (
                <span key={unitId}>
                  {unit?.name}{idx < selectedUnits.length - 1 ? ' + ' : ''}
                </span>
              );
            })}
          </div>
        )}

        {/* Reason input */}
        {selectedUnits.length >= 2 && (
          <div className="reason-input">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('feedback.combo_reason_placeholder')}
              maxLength={500}
            />
            <button
              type="button"
              className="button button-cta add-combo-btn"
              onClick={saveCombo}
              disabled={!reason.trim()}
            >
              <Plus size={18} />
              <span>{t('feedback.add_combo')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BetaFeedback() {
  const navigate = useNavigate();
  const t = useT();
  const metadata = useMetadataStore((state) => state.metadata);
  const gameVersion = metadata?.game_config?.game_version || '?';

  // Form state
  const [unitRanking, setUnitRanking] = useState(BETA_UNITS.map(u => u.id));
  const [favoriteCombos, setFavoriteCombos] = useState([]);
  const [turnRating, setTurnRating] = useState(0);
  const [likedMost, setLikedMost] = useState('');
  const [improvements, setImprovements] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync unitRanking with BETA_UNITS (handles hot reload stale state)
  useEffect(() => {
    const validIds = BETA_UNITS.map(u => u.id);
    const hasInvalidIds = unitRanking.some(id => !validIds.includes(id));
    const hasMissingIds = validIds.some(id => !unitRanking.includes(id));
    if (hasInvalidIds || hasMissingIds) {
      setUnitRanking(validIds);
    }
  }, [unitRanking]);

  // Add background class
  useEffect(() => {
    document.body.classList.add('dashboard-bg');
    return () => document.body.classList.remove('dashboard-bg');
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setUnitRanking((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitBetaFeedback({
        unit_ranking: unitRanking,
        favorite_combos: favoriteCombos,
        turn_system_rating: turnRating || null,
        liked_most: likedMost || null,
        improvement_suggestions: improvements || null,
      });

      setSubmitted(true);
      feedback.success(t('feedback.thank_you'));
    } catch (err) {
      feedback.error(err.userMessage || t('feedback.error_sending'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get unit objects in ranking order (filter out any undefined from stale state)
  const rankedUnits = unitRanking
    .map(id => BETA_UNITS.find(u => u.id === id))
    .filter(Boolean);

  if (submitted) {
    return (
      <div className="main-container main-container--betaquest">
        <MainMenu />
        <div className="betaquest-container">
          <div className="betaquest-success style-glass style-primary">
            <MessageSquareHeart size={64} />
            <h2>{t('feedback.thank_you')}</h2>
            <p>{t('feedback.opinion_helps')}</p>
            <button className="button button-cta" onClick={() => navigate('/dashboard')}>
              {t('feedback.back_to_dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container main-container--betaquest">
      <MainMenu />

      <div className="betaquest-container">
        <div className="betaquest-header">
          <h1>{t('feedback.title')}</h1>
          <span className="version-badge">v{gameVersion}</span>
        </div>

        <form onSubmit={handleSubmit} className="betaquest-form">
          {/* Unit Ranking Section */}
          <section className="style-glass style-primary betaquest-section">
            <h3>{t('feedback.section_ranking')}</h3>
            <p className="section-hint">{t('feedback.ranking_hint')}</p>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={unitRanking} strategy={verticalListSortingStrategy}>
                <div className="unit-ranking-list">
                  {rankedUnits.map((unit, index) => (
                    <SortableUnitItem key={unit.id} unit={unit} index={index} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </section>

          {/* Favorite Combos Section */}
          <section className="style-glass style-primary betaquest-section">
            <h3>{t('feedback.section_combos')}</h3>
            <p className="section-hint">{t('feedback.combos_hint')}</p>

            <ComboBuilder combos={favoriteCombos} onCombosChange={setFavoriteCombos} t={t} />
          </section>

          {/* Turn System Rating */}
          <section className="style-glass style-primary betaquest-section">
            <h3>{t('feedback.section_turn_system')}</h3>
            <p className="section-hint">{t('feedback.turn_system_hint')}</p>

            <StarRating value={turnRating} onChange={setTurnRating} />
            <p className="rating-label">
              {turnRating === 0 && t('feedback.rating_0')}
              {turnRating === 1 && t('feedback.rating_1')}
              {turnRating === 2 && t('feedback.rating_2')}
              {turnRating === 3 && t('feedback.rating_3')}
              {turnRating === 4 && t('feedback.rating_4')}
              {turnRating === 5 && t('feedback.rating_5')}
            </p>
          </section>

          {/* What they liked most */}
          <section className="style-glass style-primary betaquest-section">
            <h3>{t('feedback.section_liked_most')}</h3>
            <textarea
              value={likedMost}
              onChange={(e) => setLikedMost(e.target.value)}
              placeholder={t('feedback.liked_most_placeholder')}
              maxLength={2000}
              rows={4}
            />
          </section>

          {/* Improvement suggestions */}
          <section className="style-glass style-primary betaquest-section">
            <h3>{t('feedback.section_improvements')}</h3>
            <textarea
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              placeholder={t('feedback.improvements_placeholder')}
              maxLength={2000}
              rows={4}
            />
          </section>

          {/* Submit button */}
          <button type="submit" className="button button-cta submit-betaquest-btn" disabled={isSubmitting}>
            <Send size={20} />
            <span>{isSubmitting ? t('feedback.sending') : t('feedback.send')}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
